/**
 * We should always retain the old session IDs in an array in LocalStorage and limit the array to a certain size (to avoid exhausting the LocalStorage limit).
 * Why? Because we want to log messages before a user is even authenticated, these logs would contain a session ID.
 * In case of any issue with web client before the user could be authenticated, we can ask the user to send their old session IDs for us to do a log dive. Also we should emit these old session IDs
 * after every successful login (just in case we need to log dive into a previous pre-login issue)
 *
 * Currently the log's that are generated before the user is logged in are not sent to the server at all.
 *
 * Also, this service should read the current LOCAL_CLIENT_SESSIONS_STORAGE and concatenate the logs in this window context to the stored logs.
 *
 */
var _ = require('lodash-es');
var MAX_WAIT_TIME_FOR_LOGS_METRICS_PUSH = 5000;
angular.module('GorillaBoy.ClientMetrics')
    .factory('ClientMetricsService', [
        '$log',
        '$q',
        '$timeout',
        '$window',
        '$location',
        'EndpointConfig',
        'MetricEvents',
        'RegionService',
        'StageConfig',
        'localStorageService',
        'AppConstants',
        'GUIDGenerator',
        'BrowserService',
        '$rootScope',
        ClientMetricsService
    ]);

function ClientMetricsService($log, $q, $timeout, $window, $location, EndpointConfig, MetricEvents, RegionService, StageConfig, localStorageService, AppConstants, GUIDGenerator, BrowserService, $rootScope) {

    var region = RegionService.getRegion(),
        stage = region ? EndpointConfig.stage(region) : null,
        userInfo,
        configForStage = getConfigForRegionAndStage(region, stage),
        appLoadRecorded = false,
        pageLoadRecorded = false,
        pageVisibleRecorded = false,
        serviceCompleteRecorded = false,
        navTimingAPI = ($window.performance && window.performance.timing) ? $window.performance.timing : null,
        currentRunningLatencyEvents = {},
        clientMetricsService = {},
        LOCAL_CLIENT_SESSIONS_STORAGE = 'local_client_sessions_storage',
        PREVIOUS_SESSION_IDS_KEY = "client_metrics_service_previous_session_ids",
        STORED_CLIENT_LOG_LIMIT = 200,
        clientLogs = [],
        postSessionTimer,
        CLIENT_SESSION_POST_TIMEOUT = 3500,
        SESSION_EVENT_TYPE = {
            PAGE_VIEW: 'PAGE_VIEW',
            CLICK: 'CLICK',
            API_CALL: 'API_CALL',
            JS_OPERATION: 'JS_OPERATION',
            JS_ERROR: 'JS_ERROR',
            DOCUMENT_STATE: 'DOCUMENT_STATE',
            AUTH_EVENT: 'AUTH_EVENT',
            APP_STATE: 'APP_STATE'
        },
        postLogsPromise;

    var sessionInfo = {
        sessionId: generateNewSessionIdAndAppendToList(),
        userAgent: {
            os: BrowserService.getOS(),
            osVersion: BrowserService.getOSVersion(),
            browser: BrowserService.getBrowser(),
            browserVersion: BrowserService.getBrowserVersion(),
        }
    };

    clientMetricsService = SESSION_EVENT_TYPE;

    if (configForStage) {
        // This initializes the cognito identity provider for mobile analytics to use

        AWS.config.region = configForStage.cognitoRegion;
    }

    function generateNewSessionIdAndAppendToList() {
        var newId = GUIDGenerator.generate();
        var previousIdsArray = localStorageService.get(PREVIOUS_SESSION_IDS_KEY) || [];

        previousIdsArray.unshift(newId);
        previousIdsArray.length = 5; //MAX LIMIT OF PREVIOUS SESSION IDs

        localStorageService.set(PREVIOUS_SESSION_IDS_KEY, previousIdsArray);
        return newId;
    }


    function getConfigForRegionAndStage(region, stage) {
        if (region === 'us-gov-west-1') {
            // Do not pump metrics for PDT
            // TODO Remove this check once we have approval for
            // collecting metrics from PDT and have the app id
            // and cognito pool id for PDT
            return null;
        }

        var result = StageConfig.DEFAULT,
            configsForStage = null,
            configForStageAndRegion = null;
        if (region && stage) {
            configsForStage = StageConfig[stage];
            if (configsForStage) {
                configForStageAndRegion = configsForStage[region];
                if (configForStageAndRegion) {
                    result = configForStageAndRegion;
                }
            }
        }

        return result;
    }

    /*
     *   Save Session
     */
    function saveClientLogsImpl() {
        try {
            var existingClientLogs = localStorageService.get(LOCAL_CLIENT_SESSIONS_STORAGE) || [];
            localStorageService.set(LOCAL_CLIENT_SESSIONS_STORAGE, existingClientLogs.concat(clientLogs));
        } catch (e) {
            //Remove logs from the local storage if it is full
            localStorageService.remove(LOCAL_CLIENT_SESSIONS_STORAGE);
        }
    }

    var saveClientLogs = _.debounce(saveClientLogsImpl, 500, { trailing: true, leading: false });

    $window.addEventListener("beforeunload", function () {
        saveClientLogsImpl();
        saveClientLogs.cancel();
    });

    /*
     *   Create New Session
     */
    function createNewSession() {
        clientLogs = [];
        saveClientLogsImpl();
        $log.info('Creating New Session Metrics...');
    }

    /*
     *   Removed the logs from the current session, but
     *   keeps the current session alive.
     */
    function deleteSessionLogs() {
        clientLogs = [];

        localStorageService.remove(AppConstants.LocalStorageKeys.GLOBAL_CLIENT_ERRORS);
        localStorageService.remove(AppConstants.LocalStorageKeys.AJAX_CALL_RESULTS);
        localStorageService.remove(LOCAL_CLIENT_SESSIONS_STORAGE);
    }

    /*
     *   Post Session Metrics to Server
     */
    function postSessionMetricsToServer() {
        return $q(function (resolve, reject) {

            saveClientLogs.flush();

            // Not online, or no session, or using the old session model
            if (!navigator.onLine) {
                resolveInternal();
                return;
            }

            var currentClientErrors = clientMetricsService.getClientErrors();
            var currentAjaxCallEvents = clientMetricsService.getAjaxCallEvents();

            // No new data to upload
            if (clientLogs.length === 0 && currentAjaxCallEvents.length === 0 &&
                currentClientErrors.length === 0) {
                resolveInternal();
                return;
            }

            addClientErrorsToSession(currentClientErrors);
            addAjaxCallEventsToSession(currentAjaxCallEvents);

            logRecords = generateLogRecordsForBatch(),
                clientSessionCopy = clientLogs;
            if (stage.toLowerCase() === "dev") {
                logRecords = logRecords.filter(log => !log.Data.includes("127.0.0.1"))
            }

            deleteSessionLogs();

            if (logRecords.length > 0) {
                // Currently, the delivery stream name in NRT is named differently than all other regions
                var deliveryStreamName = 'gb-web-metrics-v3';
                if (region === 'ap-northeast-1') {
                    deliveryStreamName = 'gb-web-metrics-v2';
                }
                var logger
                if ($rootScope.metricServiceInstance) {
                    logger = $rootScope.metricServiceInstance.getLogger();
                }
                if (!!logger) {
                    logger.ldsForwardPostRequest(logRecords, deliveryStreamName).catch(() => {
                        clientLogs = clientSessionCopy.concat(clientLogs);
                        resolveInternal()
                    })

                    logger.ldsForwardPostRequest(logRecords, 'web-business-metrics').catch(() => {
                        log.error("Failed to upload records to business metrics stream");
                        resolveInternal()
                    })
                } else {
                    clientLogs = clientSessionCopy.concat(clientLogs);
                }
                
                resolveInternal()

            }

            var resolveTimeoutId = setTimeout(resolveInternal.bind(null, true), MAX_WAIT_TIME_FOR_LOGS_METRICS_PUSH);
            function resolveInternal(fromTimeout) {
                if (!fromTimeout) {
                    clearTimeout(resolveTimeoutId);
                }
                resolve();
            }
        });
    }


    function generateLogRecordsForBatch() {
        var logRecords = [];

        if (clientLogs && clientLogs.length > 0) {
            var logCount = clientLogs.length;
            for (var i = 0; i < logCount; i++) {
                logRecords.push({
                    Data: JSON.stringify({ log: clientLogs[i] })
                });
            }
        }

        return logRecords;
    }


    function addClientErrorsToSession(clientErrors) {

        if (clientErrors) {
            var numErrors = clientErrors.length;
            for (var i = 0; i < numErrors; i++) {
                var error = clientErrors[i];
                clientMetricsService.addJsErrorToSession(error.message, error.stack);
            }
        }
    }

    function addAjaxCallEventsToSession(ajaxCallEvents) {

        if (ajaxCallEvents) {
            //For each item in ajaxCallEvents array add it to session logs.
            ajaxCallEvents.forEach(clientMetricsService.addLogsToSession);
        }
    }


    function setTimeoutForSessionMetricsServerPost() {
        if (postSessionTimer) {
            return;
        }
        postSessionTimer = $timeout(function () {
            //do not use $logs here as it'll retrigger posting to the server
            postSessionTimer = null;
            postSessionMetricsToServer();
        }, CLIENT_SESSION_POST_TIMEOUT, false);
    }

    clientMetricsService.flushLogsToTheServer = function () {
        return postSessionMetricsToServer();
    };

    /*
     *   Start New Session
     *   Param: userInfo object
     *
     *   Session Model: {
     *       sessionId: {GUID String}
     *       userId: {String},
     *       userAgent: {String},
     *       logs: [{Log Model}]
     *   }
     *
     *   Log Model: {
     *       event:      {SESSION_EVENT_TYPE},
     *       metadata:   {String},
     *       success:    {Boolean},
     *       error:      {String},
     *       latency:    {Long} // Milliseconds
     *       timestamp:  {Long}  // Milliseconds
     *   }
     *
     */
    clientMetricsService.startNewSession = function () {
        if (!EndpointConfig.isMetricsEnabled()) {
            return $q.resolve();
        }

        var clientSessionString = localStorageService.get(LOCAL_CLIENT_SESSIONS_STORAGE);
        clientLogs = clientSessionString ? (clientSessionString) : null;

        // Upload any old sessions
        if (clientLogs !== null) {
            return postSessionMetricsToServer().then(function (data) {
                createNewSession(userInfo);
                return data;
            });
        } else {
            createNewSession(userInfo);
            return $q.resolve();
        }
    };

    clientMetricsService.resetLatencyCounters = function () {
        currentRunningLatencyEvents = {};
    };

    /*
     * Add logs to the client session:
     *
     *   Log Model: {
     *       event:      {SESSION_EVENT_TYPE},
     *       metadata:   {String},
     *       userId: {String},
     *       path: {String}
     *       success:    {Boolean},
     *       error:      {String},
     *       latency:    {Long} // Milliseconds
     *       timestamp:  {Long}  // Milliseconds
     *   }
     *
     */
    clientMetricsService.addLogsToSession = function (log) {
        var currentLogs = clientMetricsService.getCurrentSession();

        if (!currentLogs || !log) {
            return;
        }

        if (currentLogs.length >= STORED_CLIENT_LOG_LIMIT) {
            currentLogs.splice(0, 1);
        }

        // Update the Log with session and user info
        log.sessionId = sessionInfo.sessionId;
        log.userAgent = sessionInfo.userAgent;

        if (userInfo) {
            log.userId = userInfo.Id;
            log.organizationId = userInfo.OrganizationId;
        }

        if (!log.timestamp) {
            log.timestamp = new Date().toISOString();
        }
        log.region = region;
        log.stage = stage;

        currentLogs.push(log);
        saveClientLogs();
        setTimeoutForSessionMetricsServerPost();
    };

    /*
     * Helper Functions for addLogsToSession
     */
    clientMetricsService.addPageViewToSession = function () {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.PAGE_VIEW,
            metadata: $location.path() || null,
        });
    };

    clientMetricsService.addClickToSession = function (metadata) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.CLICK,
            metadata: metadata || null,
        });
    };

    clientMetricsService.addClickStreamToSession = function (eventData) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.CLICK,
            metadata: eventData.path || null,
            resourceId: eventData.resourceId || null,
            versionId: eventData.versionId || null,
            pageUrl: $window.location.pathname + $window.location.hash
        });
    };

    clientMetricsService.addPageViewWithDocIdToSession = function (metadata, documentId) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.PAGE_VIEW,
            metadata: metadata || null,
            documentId: documentId || null,
        });
    };

    clientMetricsService.addKeyStrokeToSession = function (metadata) {
        clientMetricsService.addLogsToSession({
            event: MetricEvents.ACTIONS.KEYSTROKE,
            metadata: metadata || null,
        });
    };

    clientMetricsService.addEventToSession = function (eventType) {
        clientMetricsService.addLogsToSession({
            event: eventType,
        });
    };

    clientMetricsService.addEventObjectToSession = function (eventObject) {
        clientMetricsService.addLogsToSession(eventObject);
    };

    clientMetricsService.addDocumentEventToSession = function (event, documentId, versionId, documentSize, documentType, numberOfPages, latency) {
        clientMetricsService.addLogsToSession({
            event: event.name,
            documentId: documentId,
            versionId: versionId,
            docVersionSize: documentSize,
            docVersionSizeKb: documentSize / 1024,
            docVersionSizeMb: documentSize / 1048576,
            documentType: documentType,
            numberOfPages: numberOfPages,
            latency: latency
        });
    };

    clientMetricsService.addApiCallToSession = function (callDescription, success, errorMessage) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.API_CALL,
            metadata: callDescription || null,
        });
    };

    clientMetricsService.addJsOperationToSession = function (operationDescription, success, errorMessage) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.JS_OPERATION,
            metadata: operationDescription || null,
        });
    };

    clientMetricsService.addDocumentStateToSession = function (stateValue) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.DOCUMENT_STATE,
            metadata: stateValue || null,
        });
    };

    clientMetricsService.addAppLoadIncognitoToSession = function (stateValue) {
        clientMetricsService.addLogsToSession({
            event: 'INCOGNITO_PAGE_LOAD',
            metadata: null,
        });
    };

    clientMetricsService.addAuthEventToSession = function (stateValue) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.AUTH_EVENT,
            metadata: stateValue || null,
        });
    };


    clientMetricsService.addJsErrorToSession = function (errorMsg, stack) {
        clientMetricsService.addLogsToSession({
            event: SESSION_EVENT_TYPE.JS_ERROR,
            metadata: errorMsg,
            stack: stack
        });
    };

    clientMetricsService.getCurrentSessionId = function () {
        return sessionInfo.sessionId;
    };

    /*
     * Get the current session
     */
    clientMetricsService.getCurrentSession = function () {
        if (!!clientLogs) {
            return clientLogs;
        } else {
            return null;
        }
    };

    /*
     * Get Client Errors
     */
    clientMetricsService.getClientErrors = function () {
        var clientErrorsString = localStorageService.get(AppConstants.LocalStorageKeys.GLOBAL_CLIENT_ERRORS);
        return (clientErrorsString) ? (clientErrorsString) : [];
    };

    /*
     * Get Ajax call events
     */
    clientMetricsService.getAjaxCallEvents = function () {
        var ajaxCallResultsString = localStorageService.get(AppConstants.LocalStorageKeys.AJAX_CALL_RESULTS);
        return (ajaxCallResultsString) ? (ajaxCallResultsString) : [];
    };


    var addAjaxCallLogsToLocalStorageDebounced = _.debounce(addAjaxCallLogsToLocalStorageImpl, 500, { trailing: true, leading: false });
    var bufferedAjaxMessages = [];
    clientMetricsService.addToAjaxCallEvents = function (logMessage) {
        bufferedAjaxMessages.push(angular.extend(logMessage, {
            path: $location.url(),
            timestamp: new Date().toISOString()
        }));
        addAjaxCallLogsToLocalStorageDebounced();
    };
    function addAjaxCallLogsToLocalStorageImpl() {
        var localStorageAjaxCallResults;
        try {
            localStorageAjaxCallResults = localStorageService.get(AppConstants.LocalStorageKeys.AJAX_CALL_RESULTS);
        } catch (e) {
            return;
        }

        if (!localStorageAjaxCallResults) {
            localStorageAjaxCallResults = [];
        }
        localStorageAjaxCallResults = localStorageAjaxCallResults.concat(bufferedAjaxMessages);
        try {
            localStorageService.set(AppConstants.LocalStorageKeys.AJAX_CALL_RESULTS, angular.toJson(localStorageAjaxCallResults));
        } catch (e) {
            // if local storage quota exceeded, remove the Ajax call results.
            localStorageService.remove(AppConstants.LocalStorageKeys.AJAX_CALL_RESULTS);
            $log.error("Error saving to LocalStorage: " + JSON.stringify(e));
        }
        bufferedAjaxMessages = [];
        setTimeoutForSessionMetricsServerPost();
    }

    /*
     * Delete Client Errors
     */
    clientMetricsService.deleteClientErrors = function () {
        localStorageService.remove(AppConstants.LocalStorageKeys.GLOBAL_CLIENT_ERRORS);
    };


    /*
     * Delete Ajax call Errors
     */
    clientMetricsService.deleteAjaxCallEvents = function () {
        localStorageService.remove(AppConstants.LocalStorageKeys.AJAX_CALL_RESULTS);
    };

    /*
     * Method to record Counts -> No. of times an event occured
     */
    clientMetricsService.recordCounterEvent = function (event, value) {
        if (!event) {
            return;
        }

        $log.debug('Recording counter event: ' + event.name + ". value: " + value);
        //Also emit these metrics to Kibana
        clientMetricsService.addLogsToSession({
            event: event.name
        });
    };



    /*
     * Method to record a latency event given the event and elapsed time
     */
    clientMetricsService.recordLatencyEvent = function (event, elapsedTime) {
        if (!event || !elapsedTime) {
            return;
        }

        $log.debug('Recording latency event: ' + event.name + ' with latency value ' + elapsedTime);
        clientMetricsService.addLogsToSession({
            event: event.name,
            latency: elapsedTime,
        });

    };

    /*
     * Method to record a document latency event given the event and elapsed time
     */
    clientMetricsService.recordDocumentLatencyEvent = function (event) {
        if (!event) {
            return;
        }

        $log.debug('Recording latency event: ' + event.name + ' with latency value ' + event.latency);
        clientMetricsService.addLogsToSession(event);

    };

    /*
     * Method to start latency event timer
     */
    clientMetricsService.startLatencyEvent = function (event) {
        if (!event || !event.name) {
            return;
        }

        // If there was already a running timer for this event, just overwrite it.
        event.startTime = performance.now();
        currentRunningLatencyEvents[event.name] = event;
        $log.debug("Started running time for latency event: " + event.name);
    };

    /*
     * Method to start latency event timer without using currentRunningLatencyEvents property (global)
     * which avoids race conditions (with lexical soping of the handle that can stopAndRecordLatency())
     * and does not require cancellation.
     */
    clientMetricsService.startNewLatencyEvent = function (logObject) {
        //make a defensive copy
        logObject = angular.extend(logObject, {});

        $log.debug("Started running time for latency event: " + logObject.event);
        // If there was already a running timer for this event, just overwrite it.
        var startTime = performance.now();
        return {
            stopAndRecordLatency: function () {
                var diff = performance.now() - startTime;
                clientMetricsService.addLogsToSession(angular.extend(logObject, {
                    latency: diff
                }));
            }
        };
    };


    /*
     * Method to stop a running latency event timer and record it
     */
    clientMetricsService.getLatencyEventStartTime = function (event) {
        if (!event || !event.name) {
            return;
        }

        var currentRunningLatencyEvent = currentRunningLatencyEvents[event.name];
        if (!currentRunningLatencyEvent || !currentRunningLatencyEvent.startTime) {
            // The event was never started
            return;
        }

        return currentRunningLatencyEvent.startTime;
    };

    clientMetricsService.getLatencyEventStartPage = function (event) {
        if (!event || !event.name) {
            return;
        }

        var currentRunningLatencyEvent = currentRunningLatencyEvents[event.name];
        if (!currentRunningLatencyEvent || !currentRunningLatencyEvent.startTime) {
            // The event was never started
            return;
        }

        return currentRunningLatencyEvent.fromPage;
    };

    /*
     * Method to stop a running latency event timer and record it
     */
    clientMetricsService.stopAndRecordLatencyEvent = function (event) {
        if (!event || !event.name) {
            return;
        }

        var currentRunningLatencyEvent = currentRunningLatencyEvents[event.name];
        if (!currentRunningLatencyEvent || !currentRunningLatencyEvent.startTime) {
            // The event was never started
            return;
        }

        var elapsedTime = performance.now() - currentRunningLatencyEvent.startTime;
        $log.debug("Stop running time and recording latency event: " + event.name);
        this.recordLatencyEvent(event, elapsedTime);
        currentRunningLatencyEvents[event.name] = null;
    };

    /*
     * Method to stop a running latency event timer and record it
     */
    clientMetricsService.stopAndRecordDocumentLatencyEvent = function (event) {
        if (!event || !event.name) {
            return;
        }

        var currentRunningLatencyEvent = currentRunningLatencyEvents[event.name];
        if (!currentRunningLatencyEvent || !currentRunningLatencyEvent.startTime) {
            // The event was never started
            return;
        }

        var elapsedTime = performance.now() - currentRunningLatencyEvent.startTime;
        $log.debug("Stop running time and recording latency event: " + event.name);
        event.latency = elapsedTime;
        this.recordDocumentLatencyEvent(event);
        currentRunningLatencyEvents[event.name] = null;
    };

    /*
     * Method to cancel a running latency event.
     */
    clientMetricsService.cancelLatencyEvent = function (event) {
        if (!event || !event.name || !currentRunningLatencyEvents[event.name]) {
            // If the event is not currently running, we don't need to cancel it.
            return;
        }

        $log.debug("Cancelling latency event: " + event.name);
        currentRunningLatencyEvents[event.name] = null;
    };

    /*
     * Measures the amount of time it takes from the moment the browser is ready to fetch the document using an HTTP request
     * to the time when all the service calls are returned and completed.
     */
    clientMetricsService.recordAppLoadServiceComplete = function () {
        if (!navTimingAPI || serviceCompleteRecorded) {
            return;
        }

        var elapsedTime = $window.Math.round(($window.performance.now() + navTimingAPI.navigationStart) - navTimingAPI.fetchStart);

        this.recordLatencyEvent(MetricEvents.APP_LOAD_SERVICE_COMPLETE_EVENT, elapsedTime);
        serviceCompleteRecorded = true;
    };

    /*
     * Measures the amount of time it takes from the moment the browser is ready to fetch the document using an HTTP request
     * to the time when a user can see and interact with the application.
     * This function should be called at that moment where the user can view the app.
     */
    clientMetricsService.recordPageVisible = function () {
        if (!navTimingAPI || pageVisibleRecorded) {
            return;
        }

        var elapsedTime = $window.Math.round(($window.performance.now() + navTimingAPI.navigationStart) - navTimingAPI.fetchStart);

        this.recordLatencyEvent(MetricEvents.PAGE_VISIBLE_EVENT, elapsedTime);
        pageVisibleRecorded = true;
    };

    /*
     * Measures the amount of time it takes from the moment the browser is ready to fetch the document using an HTTP request
     * to the end of the loadEvent, which means we have loaded all of our resources.
     */
    clientMetricsService.recordPageLoad = function () {
        if (!navTimingAPI || pageLoadRecorded) {
            return;
        }

        var self = this,
            elapsedTime = navTimingAPI.loadEventEnd - navTimingAPI.fetchStart;

        // loadEventEnd is 0 until the onload event occurs
        // if it is zero when trying to record event, retry after timeout
        if (elapsedTime < 0) {
            $timeout(this.recordPageLoad.bind(this), 500, false);
        } else {
            $log.debug('Recording page load event');
            self.recordLatencyEvent(MetricEvents.PAGE_LOAD_EVENT, elapsedTime);
            pageLoadRecorded = true;
        }
    };

    /*
     * Method to record app load event
     */
    clientMetricsService.recordAppLoad = function () {
        if (!navTimingAPI || appLoadRecorded) {
            return;
        }

        var self = this,
            elapsedTime = navTimingAPI.loadEventEnd - navTimingAPI.navigationStart;

        // loadEventEnd is 0 until the onload event occurs
        // if it is zero when trying to record event, retry after timeout
        if (elapsedTime < 0) {
            $timeout(self.recordAppLoad, 500, false);
        } else {
            $log.debug('Recording app load event');
            self.recordLatencyEvent(MetricEvents.APP_LOAD_EVENT, elapsedTime);
            appLoadRecorded = true;
        }
    };

    /* metricServiceInstance is not undefined at this moment because it's created before userInfo async calls go out.
     * metricServiceInstance has a logger which is an instance of LoggingService (for each metric we also send a log).
     * Here we set the user info for the log.
     */
    clientMetricsService.setUserInfo = function (userInfoParam) {
        userInfo = userInfoParam;

        var logger = $rootScope.metricServiceInstance.getLogger();
        if (!!logger) {
            logger.setUserId(userInfo.Id);
            logger.setOrganizationId(userInfo.OrganizationId);
        }

        clientMetricsService.addLogsToSession({
            event: "LOG_PREVIOUS_SESSION_IDS",
            metadata: (localStorageService.get(PREVIOUS_SESSION_IDS_KEY) || []).join(', ')
        });
    };

    return clientMetricsService;
}
